import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Image from '../AppImage';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location?.pathname]);

  const navigation = [
    { name: 'Home', href: '/', id: 'home' },
    { name: 'About', href: '/about-page', id: 'about' },
    { name: 'Products', href: '/product-collection', id: 'products' },
    { name: 'Contact', href: '/contact', id: 'contact' },
  ];

  const scrollToSection = (sectionId) => {
    if (location?.pathname !== '/') {
      // If not on homepage, navigate to homepage first
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 brand-transition ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md liquid-shadow' 
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <motion.div
                animate={{ 
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.05, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-14 h-14"
              >
                <Image
                  src="/assets/images/mava-1762174454410.jpg"
                  alt="Mava Cold Beverages Logo - Premium water bottle brand with leaf accent symbolizing freshness"
                  className="w-full h-full object-contain"
                />
              </motion.div>
              
              <div>
                <div className="flex flex-col">
                  <h1 className="text-2xl font-poppins font-bold text-[#FF7A00] leading-tight">
                    mava
                  </h1>
                  <p className="text-xs font-inter text-[#1A1A1A] -mt-1 leading-tight">
                    cold beverages
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigation?.map((item) => (
                <motion.div
                  key={item?.name}
                  whileHover={{ y: -2 }}
                  className="relative group"
                >
                  {item?.href?.startsWith('/') ? (
                    <Link
                      to={item?.href}
                      className={`font-poppins font-medium brand-transition ${
                        location?.pathname === item?.href
                          ? 'text-[#FF7A00]' :'text-[#1A1A1A] hover:text-[#FF7A00]'
                      }`}
                    >
                      {item?.name}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollToSection(item?.id)}
                      className="font-poppins font-medium text-[#1A1A1A] hover:text-[#FF7A00] brand-transition"
                    >
                      {item?.name}
                    </button>
                  )}
                  
                  {/* Hover indicator */}
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FF7A00] to-[#00C6FF] rounded-full"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.div>
              ))}
            </nav>

            {/* CTA Button & Mobile Menu */}
            <div className="flex items-center space-x-4">
              {/* CTA Button - Desktop */}
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('contact')}
                className="hidden lg:block bg-[#FF7A00] hover:bg-[#FF7A00]/90 text-white px-6 py-3 rounded-full font-poppins font-semibold cursor-glow water-ripple brand-transition"
              >
                Get in Touch
              </motion.button>

              {/* Mobile Menu Button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-[#FF7A00] text-white"
              >
                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Tagline Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="bg-gradient-to-r from-[#00C6FF] to-[#0072FF] text-white py-1 px-4"
        >
          <div className="container mx-auto">
            <p className="text-center text-sm font-inter font-medium">
              ✨ Sip the freshness, live the moment ✨
            </p>
          </div>
        </motion.div>
      </motion.header>
      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 bottom-0 w-80 bg-white z-40 liquid-shadow"
          >
            <div className="pt-32 px-6">
              {/* Mobile Navigation */}
              <nav className="space-y-6">
                {navigation?.map((item, index) => (
                  <motion.div
                    key={item?.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item?.href?.startsWith('/') ? (
                      <Link
                        to={item?.href}
                        className={`block text-xl font-poppins font-medium py-3 border-b border-gray-100 brand-transition ${
                          location?.pathname === item?.href
                            ? 'text-[#FF7A00]' :'text-[#1A1A1A] hover:text-[#FF7A00]'
                        }`}
                      >
                        {item?.name}
                      </Link>
                    ) : (
                      <button
                        onClick={() => scrollToSection(item?.id)}
                        className="block w-full text-left text-xl font-poppins font-medium py-3 border-b border-gray-100 text-[#1A1A1A] hover:text-[#FF7A00] brand-transition"
                      >
                        {item?.name}
                      </button>
                    )}
                  </motion.div>
                ))}
              </nav>

              {/* Mobile Contact Info */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-12 space-y-4"
              >
                <h3 className="font-poppins font-bold text-[#1A1A1A] text-lg mb-4">
                  Contact Us
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Phone size={18} className="text-[#FF7A00]" />
                    <span className="font-inter">+91 98765 43210</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Mail size={18} className="text-[#FF7A00]" />
                    <span className="font-inter">hello@mava.in</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600">
                    <MapPin size={18} className="text-[#FF7A00]" />
                    <span className="font-inter">Mumbai, India</span>
                  </div>
                </div>
              </motion.div>

              {/* Mobile CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-8"
              >
                <button
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-gradient-to-r from-[#FF7A00] to-[#00C6FF] text-white py-4 rounded-2xl font-poppins font-semibold cursor-glow"
                >
                  Get in Touch
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Mobile Menu Backdrop */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMenuOpen(false)}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;